import React from "react";
import Box from "@mui/material/Box";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DatePicker } from "@mui/x-date-pickers";
import { Moment } from "moment";
import {
  HISTORY_DATE_RANGE_OPTIONS,
  HistoryDateRangePreset,
} from "./historyDateRange";

const toggleSx = {
  flexWrap: "wrap",
  gap: 0.75,
  height: 32,
  "& .MuiToggleButtonGroup-grouped": {
    border: "1px solid #E2E8F0 !important",
    borderRadius: "6px !important",
    textTransform: "none",
    fontWeight: 600,
    fontFamily: '"Source Sans 3", sans-serif',
    fontSize: 12,
    lineHeight: 1.2,
    px: 1,
    py: 0,
    height: 32,
    color: "#475569",
    bgcolor: "#FFFFFF",
    "&.Mui-selected": {
      bgcolor: "#2563EB",
      color: "#FFFFFF",
      borderColor: "#2563EB !important",
      "&:hover": { bgcolor: "#1D4ED8" },
    },
    "&:hover": { bgcolor: "#F1F5F9" },
  },
} as const;

const datePickerSlotProps = {
  textField: {
    size: "small" as const,
    sx: {
      width: 132,
      minWidth: 132,
      "& .MuiInputBase-root": {
        height: 32,
        fontSize: 12,
        borderRadius: "6px",
      },
      "& .MuiInputLabel-root": {
        fontSize: 12,
      },
      "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
        transform: "translate(14px, 7px) scale(1)",
      },
      "& .MuiOutlinedInput-input": {
        py: 0.5,
        fontSize: 12,
      },
    },
  },
};

interface IHistoryDateRangeFilterProps {
  dateRange: HistoryDateRangePreset;
  fromDate: Moment;
  toDate: Moment;
  onPresetChange: (preset: Exclude<HistoryDateRangePreset, "custom">) => void;
  onFromDateChange: (date: Moment | null) => void;
  onToDateChange: (date: Moment | null) => void;
}

export const HistoryDateRangeFilter: React.FC<IHistoryDateRangeFilterProps> = ({
  dateRange,
  fromDate,
  toDate,
  onPresetChange,
  onFromDateChange,
  onToDateChange,
}) => {
  return (
    <Box
      sx={{
        mt: 1.5,
        mb: 1.5,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
      }}
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={dateRange === "custom" ? null : dateRange}
        onChange={(_event, value: Exclude<HistoryDateRangePreset, "custom"> | null) => {
          if (value) onPresetChange(value);
        }}
        aria-label="Date range"
        sx={toggleSx}
      >
        {HISTORY_DATE_RANGE_OPTIONS.map((option) => (
          <ToggleButton key={option.value} value={option.value}>
            {option.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <LocalizationProvider dateAdapter={AdapterMoment}>
        <DatePicker
          value={fromDate}
          onChange={onFromDateChange}
          format="MM/DD/YYYY"
          label="From"
          maxDate={toDate || undefined}
          slotProps={datePickerSlotProps}
        />
        <DatePicker
          value={toDate}
          onChange={onToDateChange}
          format="MM/DD/YYYY"
          label="To"
          minDate={fromDate || undefined}
          slotProps={datePickerSlotProps}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default HistoryDateRangeFilter;
